import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Replay as ReplayIcon,
  Cancel as CancelIcon,
  Payments as PaymentsIcon,
  CheckCircle as CheckCircleIcon,
  ContactSupport as ContactSupportIcon,
} from '@mui/icons-material';

const RefundAndCancellationPolicy = () => {
  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.05)',
          }}
        >
          {/* Header */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'primary.main',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Refund & Cancellation Policy
          </Typography>

          {/* <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: {new Date().toLocaleDateString()}
          </Typography> */}

          <Divider sx={{ mb: 4 }} />

          {/* Intro */}
          <Typography variant="body1" paragraph>
            This Refund & Cancellation Policy outlines the terms under which refunds and cancellations
            are handled for property bookings, services, and transactions conducted through our
            real estate platform. Please read this policy carefully before making any payment.
          </Typography>

          {/* Section 1 */}
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Booking Cancellation
            </Typography>
            <List dense>
              {[
                'Property booking cancellations must be requested in writing or via registered account.',
                'Cancellations are subject to verification and approval.',
                'Cancellation timelines may vary based on the project and developer terms.',
              ].map((item, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CancelIcon color="primary" />
                  </ListItemIcon>
                  <Typography variant="body2">{item}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Section 2 */}
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Refund Policy
            </Typography>
            <List dense>
              {[
                'Refunds are processed only after successful cancellation approval.',
                'Refund amounts depend on the booking stage and agreement terms.',
                'Refunds, if applicable, will be processed within 7–14 working days.',
                'Refunds are credited to the original payment method.',
              ].map((item, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ReplayIcon color="primary" />
                  </ListItemIcon>
                  <Typography variant="body2">{item}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Section 3 */}
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Token & Advance Amount
            </Typography>
            <List dense>
              {[
                'Token or booking amounts are generally non-refundable.',
                'Advance payments may be partially refundable as per developer policy.',
                'Government charges, taxes, and registration fees are non-refundable.',
              ].map((item, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <PaymentsIcon color="primary" />
                  </ListItemIcon>
                  <Typography variant="body2">{item}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Accordion Section */}
          <Accordion elevation={0} sx={{ mb: 3, border: '1px solid rgba(0,0,0,0.12)' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Non-Refundable Scenarios</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {[
                  'Delay caused due to customer documentation issues.',
                  'Change of mind after agreement execution.',
                  'Failure to arrange financing or loan approval.',
                  'Violation of platform or legal terms.',
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <Typography variant="body2">{item}</Typography>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion elevation={0} sx={{ mb: 4, border: '1px solid rgba(0,0,0,0.12)' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Policy Changes</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                We reserve the right to modify or update this Refund & Cancellation Policy at any time.
                Changes will be effective immediately upon posting on this page.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Help Section */}
          <Box
            sx={{
              mt: 6,
              p: 4,
              borderRadius: 3,
              backgroundColor: 'rgba(63, 81, 181, 0.05)',
              border: '1px solid rgba(63, 81, 181, 0.1)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              Need Help with a Refund or Cancellation?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Our support team is available to assist you with refund status, cancellations,
              and related queries.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ContactSupportIcon />}
              sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
            >
              Contact Support
            </Button>
          </Box>
        </Paper>

        {/* <Box textAlign="center" sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Estate Platform. All rights reserved.
          </Typography>
        </Box> */}
      </Container>
    </Box>
  );
};

export default RefundAndCancellationPolicy;
